import {
  ProcedimentoConTask,
  RichiestaDTO,
  getGetFrontAreaPubblicaRichiesteIdModuliIdQueryOptions,
  getGetFrontRichiesteIdModuliIdQueryOptions,
  useGetBackProcedimentiIdInfoGenerali,
  useGetQuery,
} from '@framework/api-client';
import { useMemo, useRef } from 'react';
import { useLocation } from 'react-router';
import { useControlledAccessSubmission } from '../../submission/hooks/useControlledAccessSubmission';
import { isPublicSubmission } from '../../submission/isPublicSubmission';
import {
  IUseFrontOfficeModuleResult,
  useFrontOfficeModule,
} from './useFrontOfficeModule';

/**
 * Ritorna l'API corretta da chiamare in base al path fornito.
 * Nel caso di un path che contiene 'osservazioni' ritorna l'endpoint per le richieste pubbliche.
 * Altrimenti, ritorna l'endpoint per le richieste dell'utente.
 */
function getSubmissionModuleEndpoint(path: string) {
  if (isPublicSubmission(path)) {
    // GET/area-pubblica/richieste/{id}/moduli
    return getGetFrontAreaPubblicaRichiesteIdModuliIdQueryOptions;
  }

  // GET/richesta/{id}/moduli
  return getGetFrontRichiesteIdModuliIdQueryOptions;
}

/**
 * Ritorna la richiessta in base all'id fornito, richiamando l'API
 * "privata" se si sta guardando una propria richiesta, o quella "pubblica"
 * se si cerca una richiesta pubblica.
 */
function useControlledAccessModule(
  submissionId: string | null | undefined,
  moduleId: string | null | undefined,
) {
  const location = useLocation();

  const queryOptions = useMemo(
    () => getSubmissionModuleEndpoint(location.pathname),
    [location.pathname],
  );

  const result = useGetQuery(queryOptions, [
    submissionId!,
    moduleId!,
    {
      query: {
        keepPreviousData: true,
        enabled: Boolean(submissionId) && Boolean(moduleId),
      },
    },
  ]);

  return result;
}

export interface IUseModuleData {
  /** Indica il modulo salvato in questi dati */
  currentSubmissionModuleId?: string | null;
  module?: IUseFrontOfficeModuleResult | undefined;
  submission?: RichiestaDTO | null | undefined;
  dossier?: ProcedimentoConTask | null | undefined;
}

export function useModule(params: {
  submissionId: string | null | undefined;
  submissionModuleId: string | null | undefined;
}) {
  const { submissionId, submissionModuleId } = params;

  const { data, error, isLoading, isFetching } = useControlledAccessModule(
    submissionId,
    submissionModuleId,
  );

  const submissionRequest = useControlledAccessSubmission(submissionId);

  const module = useFrontOfficeModule({ data });

  // TODO -> i dati saranno recuperati già dalla submission stessa
  const dossierResult = useGetBackProcedimentiIdInfoGenerali(
    submissionRequest.data?.idIstanzaProcedimentoPratica ?? '<unknown>',
    {
      query: {
        enabled: !!submissionRequest.data?.idIstanzaProcedimentoPratica,
      },
    },
  );

  const dataRef = useRef<IUseModuleData>({
    module,
    submission: submissionRequest.data,
    dossier: dossierResult.data?.data,
    currentSubmissionModuleId: submissionModuleId,
  });

  // Se cambia il modulo richiesto, aggiorniamo i dati
  if (dataRef.current.currentSubmissionModuleId !== submissionModuleId) {
    dataRef.current = {
      module,
      submission: submissionRequest.data,
      dossier: dossierResult.data?.data,
      currentSubmissionModuleId: submissionModuleId,
    };
  }

  /**
   * Gestiamo il refetch come un'unica operazione, in modo da evitare di
   * avere dati aggiornati parzialmente.
   * Questo evita anche dei glitch visivi dovuti al re-rendering, e soprattutto
   * garantisce il corretto aggiornamento degli `initialValues` del `ModuleForm`.
   */
  if (
    !isFetching &&
    !dossierResult.isFetching &&
    !submissionRequest.isFetching
  ) {
    dataRef.current = {
      module,
      submission: submissionRequest.data,
      dossier: dossierResult.data?.data,
      currentSubmissionModuleId: submissionModuleId,
    };
  }

  return {
    ...dataRef.current,
    error: error ?? submissionRequest.error ?? dossierResult.error,
    isLoading:
      isLoading || submissionRequest.isLoading || dossierResult.isLoading,
    isFetching:
      isFetching || submissionRequest.isFetching || dossierResult.isFetching,
  };
}

export type IUseModule = ReturnType<typeof useModule>;
export type IFrontOfficeModule = NonNullable<IUseModule['module']>;
